import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    TicketContent,
    TicketContentText,
    TicketTitle,
} from './TicketPlusLayout';
import {
    FullWidthBottomButton,
    FullWidthBottomButtonColor,
} from '../FullWidthBottomButton.tsx';
import { isRunningInNativeWrapper } from '../../../Native.ts';
import { useNavigate } from 'react-router-dom';
import {
    TicketReceipt,
    TicketReceiptType,
} from 'dg-web-shared/model/Ticket.ts';

export function PayStationPaymentReceiptView({
    ticketReceipt,
    hasPaidPayment,
    hasDownloadableReceipt,
    showTicketPlusUnavailableMessage,
}: {
    ticketReceipt: TicketReceipt;
    hasPaidPayment: boolean;
    hasDownloadableReceipt: boolean;
    showTicketPlusUnavailableMessage: boolean;
}) {
    const navigate = useNavigate();
    return (
        <TicketContent>
            <TicketTitle>
                <Localized de="Quittung" fr="Reçu" it="Ricevuta" en="Receipt" />
            </TicketTitle>
            {hasPaidPayment ? (
                <>
                    <TicketContentText>
                        {showTicketPlusUnavailableMessage ? (
                            <Localized
                                de="Dieses Ticket wurde bereits an der Kasse verarbeitet. Ticket+ kann deshalb nicht mehr aktiviert werden. Falls inzwischen weitere Parkgebühren angefallen sind, bezahlen Sie diese bitte an der Kasse."
                                fr="Ce ticket a déjà été traité à la caisse. Ticket+ ne peut donc plus être activé. Si des frais de stationnement supplémentaires se sont accumulés entre-temps, veuillez les régler à la caisse."
                                it="Questo ticket è già stato elaborato alla cassa. Ticket+ non può quindi più essere attivato. Se nel frattempo sono maturati ulteriori costi di parcheggio, si prega di pagarli alla cassa."
                                en="This ticket has already been processed at the pay station. Ticket+ can therefore no longer be activated. If additional parking fees have accrued in the meantime, please pay them at the pay station."
                            />
                        ) : hasDownloadableReceipt ? (
                            <Localized
                                de="Hier können Sie die Quittung zum Parkvorgang, den Sie an der Kasse bezahlt haben, herunterladen."
                                fr="Ici, vous pouvez télécharger le reçu de la transaction de stationnement que vous avez payée à la caisse."
                                it="Qui potrai scaricare la ricevuta della transazione di parcheggio che hai pagato alla cassa."
                                en="Here you can download the receipt for the parking transaction that you paid for at the pay station."
                            />
                        ) : (
                            <Localized
                                de="Für dieses Ticket ist keine Quittung zum Herunterladen verfügbar."
                                fr="Aucun reçu n'est disponible au téléchargement pour ce ticket."
                                it="Non è disponibile alcuna ricevuta da scaricare per questo ticket."
                                en="No receipt is available to download for this ticket."
                            />
                        )}
                    </TicketContentText>
                    {hasDownloadableReceipt && (
                        <FullWidthBottomButton
                            color={FullWidthBottomButtonColor.BLUE}
                            onClick={() =>
                                window.open(
                                    getTicketReceiptUrl(ticketReceipt),
                                    '_blank',
                                    'noreferrer',
                                )
                            }
                            label={{
                                de: 'Quittung herunterladen',
                                fr: 'Télécharger le reçu',
                                it: 'Scarica la ricevuta',
                                en: 'Download receipt',
                            }}
                        />
                    )}
                </>
            ) : (
                <TicketContentText>
                    <Localized
                        de="Wenn Sie die Zahlung an der Kasse abgeschlossen haben, können Sie hier die Quittung herunterladen."
                        fr="Lorsque vous avez terminé le paiement à la caisse, vous pouvez télécharger le reçu ici."
                        it="Dopo aver completato il pagamento alla cassa, puoi scaricare qui la ricevuta."
                        en="Once you have completed the pay station payment, you can download the receipt here."
                    />
                </TicketContentText>
            )}
            {isRunningInNativeWrapper() && (
                <FullWidthBottomButton
                    color={FullWidthBottomButtonColor.WHITE}
                    onClick={() => navigate('/')}
                    label={{
                        de: 'Schliessen',
                        fr: 'Fermer',
                        it: 'Chiudi',
                        en: 'Close',
                    }}
                />
            )}
        </TicketContent>
    );
}

function getTicketReceiptUrl(ticketReceipt: TicketReceipt) {
    switch (ticketReceipt.receiptType) {
        case TicketReceiptType.PUNCH_CARD: {
            return `/ui-api/pay-station/ticket-payment/${ticketReceipt.ticketBase64}/pdf`;
        }
        case TicketReceiptType.QR_TICKET:
            return `/ui-api/pay-station/ticket-payment-qr/${ticketReceipt.ticketId}/pdf`;
    }
}
